import React, { useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "Components/PageHeader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Typography } from "antd";
import { SbTable, SbTag, SbSelect, SbButton, SbNotification, SbTooltip, CaretDownOutlined } from "uiCore";
import { getTalentMobilityApplications } from "Api/apiService";
import {
  PageContainerStyled,
} from "styles/common.styles";
import { ClickableTableWrapperStyled } from "./sharedTable.styles";
import { getFilters, buildPersonColumns } from "./RedeploymentCenterV2/RedeploymentCenterV2.utils";
import { PageTransitionStyled } from "./talentMobility.animations";
import { SliderRowStyled } from "./RoleDetailV2.styles";
import SkillGapAccordion from "./components/SkillGapAccordion";
import { IndexInfoLabel } from "./components/IndexTooltip";
import LearningCatalystLink from "./components/LearningCatalystLink";
import CriticalSkillTags, { getPrioritySkillsFilterValue } from "./components/PrioritySkillTags";
import ShortlistTag from "./components/ShortlistTag";
import { useRoleSuccessors, useSkillGapForPair } from "./useTalentMobilityData";
import { getCriticalSkillGaps } from "./mockData/talentMobility.mock";
import { getScoreColor } from "Utils/scoreColor.utils";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import { SUCCESSOR_TOP_N_OPTIONS, TEXT } from "./talentMobility.constants";
import { CROSS_PAGE_TOUR_PHASE, getTourLegSteps } from "./TalentMobilityManagerV2/TalentMobilityManagerV2.constants";
import useTourStore from "Store/useTourStore";
import SbTourLeg from "uiCore/SbTour/SbTourLeg";
import { TourTargetStyled } from "uiCore/SbTour/SbTour.styles";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Text } = Typography;

// Row-level wrapper so each candidate's skill gap can be computed lazily via
// the shared hook (keeps RoleDetailV2 itself free of per-row hook calls).
const CandidateExpandedRow = ({ roleId, personId, sri, occupantUserId, openOccupants, shortlisted }) => {
  const { skillGap, isLoading } = useSkillGapForPair(roleId, personId);
  return (
    <SkillGapAccordion
      title="Skill Gap vs. This Role"
      matchPct={sri}
      skillGap={skillGap}
      roleId={roleId}
      personId={personId}
      occupantUserId={occupantUserId}
      openOccupants={openOccupants}
      shortlisted={shortlisted}
      indexType="smi"
      loading={isLoading}
      skillLimit={5}
    />
  );
};

// Both tables on this page expand a row into CandidateExpandedRow's skill
// gap, and neither shows an expand column of its own (showExpandColumn is
// false — the whole row is the click target). Without an affordance nothing
// tells a user the row opens at all, so both get the same trailing caret
// column, rotated 180 when open, that Agent Library's group table uses
// (SbAgentGroupTableV2) and that Jobs Library renders as an up/down
// triangle. Presentational only — the row's own onClick does the toggling.
const buildExpandColumn = (expandedKeys, getRowKey) => ({
  title: "Skill Gap",
  key: "expand",
  width: 90,
  align: "center",
  onHeaderCell: () => ({ className: "tm-col-header-center" }),
  render: (_, record) => <CaretDownOutlined rotate={expandedKeys.includes(getRowKey(record)) ? 180 : 0} />,
});

// GET /talent-mobility/applications rows scoped to this role's JD — who
// applied via the employee page's Apply button, their SMI against it, and
// when. The role is fixed per view, so no "Applied For" column. Employee ID
// shows the HRIS emp_id (nullable → "—"), never the opaque user_id — that
// one only drives the expanded skill-gap fetch. thresholds color the SMI tag
// the same way the candidates table's SMI Match column does; smi is null
// until a people_profiles row exists for the pair.
const buildApplicationColumns = (thresholds, rows, expandedKeys) => [
  {
    title: "Employee ID",
    dataIndex: "emp_id",
    key: "emp_id",
    filters: getFilters(rows, "emp_id"),
    filterSearch: true,
    onFilter: (value, record) => record.emp_id === value,
    render: (empId) => empId || "—",
  },
  {
    title: "Applicant",
    dataIndex: "name",
    key: "name",
    filters: getFilters(rows, "name"),
    filterSearch: true,
    onFilter: (value, record) => record.name === value,
    render: (name, record) => (
      <div>
        <div style={{ fontWeight: 600 }}>{name || "—"}</div>
        {record.email && <div style={{ fontSize: 11.5, color: "#5A6A7A" }}>{record.email}</div>}
      </div>
    ),
  },
  {
    title: <IndexInfoLabel index="SMI" />,
    dataIndex: "smi",
    key: "smi",
    filterLabel: (smi) => `${Math.round(smi)}%`,
    render: (smi) =>
      smi != null ? <SbTag color={getScoreColor(smi, thresholds)}>{Math.round(smi)}%</SbTag> : "—",
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    filters: getFilters(rows, "status"),
    filterSearch: true,
    onFilter: (value, record) => record.status === value,
    render: (status) => <SbTag color="processing">{status || "—"}</SbTag>,
  },
  {
    title: "Applied On",
    dataIndex: "applied_at",
    key: "applied_at",
    sorter: (a, b) => new Date(a.applied_at || 0) - new Date(b.applied_at || 0),
    render: (appliedAt) => (appliedAt ? new Date(appliedAt).toLocaleDateString() : "—"),
  },
  buildExpandColumn(expandedKeys, (record) => record.sb_application_id),
];

const ANALYTICS_FROM = "talent_mobility_role_detail";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.ROLE_DETAIL_VIEWED;

const RoleDetailV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const { roleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Every current occupant this role was opened with, and the role's own JD
  // metadata (title/function/subFunction/location/level) travel as router
  // state, not the URL — TalentMobilityManagerV2's Title link already has
  // both (it's what built the row being clicked), so this page doesn't need
  // its own fetch just to re-derive them. A direct/bookmarked visit (no
  // state) has neither — same "Role not found" fallback a stale/hand-edited
  // old URL used to hit.
  const occupantIds = location.state?.occupantIds ?? [];
  const passedRole = location.state?.role ?? null;
  // Each of those same occupants' own Open/Discoverable snapshot — carried
  // straight through to the Candidate Profile page's Shortlist action (see
  // SkillGapAccordion's navigate call below), which sends it on to POST
  // /talent-mobility/shortlist's `user_ids` batch.
  const openOccupants = location.state?.openOccupants ?? [];
  // selectedOccupantId defaults to the roster's first occupant (see
  // useRoleSuccessors) — the candidates table's shortlisted flags/actions
  // below come from the JD's own shared shortlist (one row per role), so
  // this id no longer scopes them; it's only carried into each expanded
  // row's "View Detailed Profile" link (as ?user_id=) for that page's own
  // teammate-exclusion. There's no occupant-switcher UI, so it never
  // changes after that.
  const { role, selectedOccupantId, candidates, totalCandidates, topN, setTopN, isLoading } = useRoleSuccessors(
    roleId,
    occupantIds,
    passedRole
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const thresholds = useMatchThresholds();
  // "all" = the candidates table (default), "applications" = who applied to
  // this role's JD via the employee page's Apply button.
  const [view, setView] = useState("all");
  const [applications, setApplications] = useState(null); // null = loading
  const [expandedApplicationKeys, setExpandedApplicationKeys] = useState([]);

  // ── Guided tour: Role Detail leg (handed off from the Talent Mobility page) ──
  // Next opens the top-ranked candidate's profile for the Candidate Profile
  // leg — or, with nobody scored against this role, goes straight back to
  // the Talent Mobility page's Planning Tools step.
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const viewToggleRef = useRef(null);
  const endRoleDetailTour = () => {
    setCrossPageTourPhase(CROSS_PAGE_TOUR_PHASE.FINISH);
    navigate("/talent-mobility");
  };
  const continueToCandidateTour = () => {
    const [topCandidate] = candidates || [];
    if (!topCandidate) {
      setCrossPageTourPhase(CROSS_PAGE_TOUR_PHASE.PLANNING_TOOLS);
      navigate("/talent-mobility");
      return;
    }
    setCrossPageTourPhase(CROSS_PAGE_TOUR_PHASE.CANDIDATE);
    // Same URL + state SkillGapAccordion's "View Detailed Profile" builds.
    navigate(
      `/talent-mobility/candidate/${topCandidate.personId}/role/${roleId}?index=smi${
        selectedOccupantId ? `&user_id=${selectedOccupantId}` : ""
      }`,
      openOccupants.length ? { state: { openOccupants } } : undefined
    );
  };

  const toggleApplicationRow = (key) =>
    setExpandedApplicationKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  // The failure toast is global and sits until dismissed — a fetch that
  // fails after navigation/logout must not fire it over another screen.
  const isMountedRef = useRef(true);
  useEffect(() => {
    // Reset on every run so a remounted effect (e.g. StrictMode's dev
    // double-invoke) doesn't leave the flag stuck false.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Refetched on every switch so the table always shows current
  // applications for THIS role's JD.
  const showApplications = async () => {
    setView("applications");
    setApplications(null);
    setExpandedApplicationKeys([]);
    try {
      const response = await getTalentMobilityApplications(roleId);
      setApplications(Array.isArray(response?.applications) ? response.applications : []);
    } catch (fetchError) {
      if (!isMountedRef.current) return;
      setApplications([]);
      SbNotification.error(
        "Unable to load applications",
        fetchError?.response?.data?.message || "Please try again."
      );
    }
  };

  const applicationColumns = useMemo(
    () => buildApplicationColumns(thresholds, applications || [], expandedApplicationKeys),
    [thresholds, applications, expandedApplicationKeys]
  );

  // Rebuilt only when its inputs change — the filter option lists walk the
  // full candidate set, and this page re-renders on every row expand/toggle.
  const candidateColumns = useMemo(() => {
    const getCriticalGaps = (record) => record.criticalGaps ?? getCriticalSkillGaps(roleId, record.personId);
    // getFilters reads flat keys, so the nested person fields are flattened
    // once for the person-based filter list below.
    const candidatePersons = (candidates || []).map((candidate) => candidate.person || {});
    return [
      { title: "#", key: "rank", width: 50, render: (_, __, i) => i + 1 },
      ...buildPersonColumns(candidates || [], {
        nameTitle: "Candidate",
        renderName: (_, record) => (
          <>
            {record.person?.name}
            <ShortlistTag roleId={roleId} personId={record.personId} shortlisted={record.shortlisted} />
          </>
        ),
      }),
      {
        title: "Current Role",
        key: "currentRole",
        filters: getFilters(candidatePersons, "currentRoleTitle"),
        filterSearch: true,
        onFilter: (value, record) => record.person?.currentRoleTitle === value,
        render: (_, record) => record.person?.currentRoleTitle || "—",
      },
      {
        title: <IndexInfoLabel index="SMI">SMI Match</IndexInfoLabel>,
        dataIndex: "sri",
        align: "center",
        // Not sortable — useRoleSuccessors already hands rows over ranked by
        // SMI, highest first, so the "#" column is the rank.
        filterLabel: (sri) => `${sri}%`,
        render: (sri) =>
          // null here means the candidate hasn't updated their profile yet
          // (no skills to score against the role), not a genuinely
          // computed rock-bottom match — show a dash instead of "0%" so
          // it doesn't read as an actual (if poor) match.
          sri == null ? (
            <SbTooltip content="User has not updated profile">—</SbTooltip>
          ) : (
            <SbTag color={getScoreColor(sri, thresholds)} style={{ fontSize: 24, fontWeight: 700, padding: "4px 12px" }}>
              {sri}%
            </SbTag>
          ),
      },
      {
        title: "Critical Skills Gaps",
        key: "criticalSkills",
        filterValue: (record) => getPrioritySkillsFilterValue(getCriticalGaps(record)),
        render: (_, record) => <CriticalSkillTags criticalGaps={getCriticalGaps(record)} />,
      },
      buildExpandColumn(expandedRowKeys, (record) => record.personId),
    ];
  }, [candidates, thresholds, roleId, expandedRowKeys]);

  if (!role) {
    return (
      <PageContainerStyled>
        <PageHeader
          title="Role not found"
        />
      </PageContainerStyled>
    );
  }

  const toggleRow = (personId) =>
    setExpandedRowKeys((prev) => (prev.includes(personId) ? prev.filter((k) => k !== personId) : [...prev, personId]));

  return (
    <PageTransitionStyled>
      <PageHeader
        title={
          <>
            {role.title} — Potential Successors
          </>
        }
        subtitle={
          <>
            <Text type="secondary">
              {role.function} · {role.subFunction} · {role.location} · {role.level}
            </Text>
          </>
        }
        onBack={() => navigate("/talent-mobility")}
        backLabel={TEXT.BACK_TO_ROLES}
        actions={
          <>
            <LearningCatalystLink />
          </>
        }
      />

      <div style={{ padding: "16px 24px 0" }}>
        <SliderRowStyled>
          <span className="label">
            Show top of {totalCandidates} candidates
          </span>
          <div className="slider-wrap">
            <SbSelect
              style={{ width: 120 }}
              value={topN}
              onChange={setTopN}
              options={SUCCESSOR_TOP_N_OPTIONS.map((n) => ({ value: n, label: `Top ${n}` }))}
            />
            <TourTargetStyled ref={viewToggleRef} style={{ gap: 8 }}>
              <SbButton buttonType={view === "all" ? "primary" : "secondary"} onClick={() => setView("all")}>
                {TEXT.BTN_ALL}
              </SbButton>
              <SbButton buttonType={view === "applications" ? "primary" : "secondary"} onClick={showApplications}>
                {TEXT.BTN_APPLICATIONS}
              </SbButton>
            </TourTargetStyled>
          </div>
        </SliderRowStyled>
      </div>

      {view === "applications" ? (
        <ClickableTableWrapperStyled>
          <SbTable
            columns={applicationColumns}
            dataSource={applications || []}
            loading={applications === null}
            rowKey="sb_application_id"
            pagination={false}
            sticky
            onRow={(record) => ({ onClick: () => toggleApplicationRow(record.sb_application_id) })}
            expandable={{
              expandedRowKeys: expandedApplicationKeys,
              showExpandColumn: false,
              rowExpandable: () => true,
              // Same inline skill-gap view the candidates table expands to —
              // the applicant is the person, matched against THIS role's JD.
              expandedRowRender: (record) => (
                <CandidateExpandedRow
                  roleId={roleId}
                  personId={record.user_id}
                  sri={record.smi}
                  occupantUserId={selectedOccupantId}
                  openOccupants={openOccupants}
                />
              ),
            }}
          />
        </ClickableTableWrapperStyled>
      ) : (
        <ClickableTableWrapperStyled>
          <SbTable
            columns={candidateColumns}
            dataSource={candidates}
            loading={isLoading}
            rowKey={(record) => record.personId}
            pagination={false}
            sticky
            onRow={(record) => ({ onClick: () => toggleRow(record.personId) })}
            expandable={{
              expandedRowKeys,
              showExpandColumn: false,
              rowExpandable: () => true,
              expandedRowRender: (record) => (
                <CandidateExpandedRow
                  roleId={roleId}
                  personId={record.personId}
                  sri={record.sri}
                  occupantUserId={selectedOccupantId}
                  openOccupants={openOccupants}
                  shortlisted={record.shortlisted}
                />
              ),
            }}
          />
        </ClickableTableWrapperStyled>
      )}

      {/* Role Detail leg of the Talent Mobility tour */}
      <SbTourLeg
        active={crossPageTourPhase === CROSS_PAGE_TOUR_PHASE.ROLE_DETAIL && !isLoading}
        ready={!isLoading}
        steps={getTourLegSteps(CROSS_PAGE_TOUR_PHASE.ROLE_DETAIL)}
        resolveTarget={() => viewToggleRef.current}
        onNext={continueToCandidateTour}
        onClose={endRoleDetailTour}
      />
    </PageTransitionStyled>
  );
};

export default RoleDetailV2;
